import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaCheckCircle } from 'react-icons/fa';
import { getOrderBySessionId, reset } from '../features/order/orderSlice';
import Loading from '../components/Loading';
import { formatPrice as formatCurrency } from '../utils/currency';
import { centsToAnalyticsAmount, trackEvent } from '../utils/analytics';

function OrderSuccess() {
    const { sessionId: sessionIdParam } = useParams();
    const dispatch = useDispatch();
    const { order, isLoading, isError, message } = useSelector((state) => state.order);
    const purchaseTrackedRef = React.useRef(false);

    const sessionId = React.useMemo(() => {
        // Prefer hash fragment to avoid leaking sensitive IDs via Referer headers.
        const hash = window.location.hash || '';
        const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
        return hashParams.get('session_id') || sessionIdParam || '';
    }, [sessionIdParam]);

    useEffect(() => {
        // Enforce no-referrer while on success page to avoid leaking IDs to third-party requests.
        const existingReferrerMeta = document.querySelector('meta[name="referrer"]');
        const originalContent = existingReferrerMeta?.getAttribute('content');
        if (existingReferrerMeta) {
            existingReferrerMeta.setAttribute('content', 'no-referrer');
        } else {
            const meta = document.createElement('meta');
            meta.name = 'referrer';
            meta.content = 'no-referrer';
            document.head.appendChild(meta);
        }

        if (sessionId) {
            dispatch(getOrderBySessionId(sessionId));
        }
        
        return () => {
            if (existingReferrerMeta && originalContent !== null) {
                existingReferrerMeta.setAttribute('content', originalContent);
            } else if (!existingReferrerMeta) {
                const created = document.querySelector('meta[name="referrer"][content="no-referrer"]');
                if (created) {
                    document.head.removeChild(created);
                }
            }
            dispatch(reset());
        };
    }, [sessionId, dispatch]);

    useEffect(() => {
        if (!order || purchaseTrackedRef.current) return;

        const paymentStatus = order.payment_status || order.status || 'processing';
        if (paymentStatus !== 'paid') return;

        purchaseTrackedRef.current = true;
        trackEvent('purchase', {
            transaction_id: order.order_num,
            value: centsToAnalyticsAmount(order.total),
            currency: (order.currency || 'eur').toUpperCase(),
            shipping_tier: order.shipping_method,
            items: (order.items || []).map((item, index) => ({
                item_id: `${order.order_num}-${index + 1}`,
                item_name: item.name,
                item_variant: item.variant_name,
                quantity: item.quantity,
            })),
        });
    }, [order]);

    if (isLoading) return <Loading />;

    if (isError) {
        return (
            <div className="container" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <h1>Something went wrong</h1>
                <p>{message || "We couldn't find your order. Please contact support if you need assistance."}</p>
                <Link to="/" className="btn btn-primary">Go to Home</Link>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="container" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <h1>Finalizing your order…</h1>
                <p style={{ color: 'var(--color-ink-soft)', marginTop: '1rem' }}>
                    We're confirming your payment with Stripe. This may take a few seconds.
                </p>
                <Loading />
            </div>
        );
    }

    const formatPrice = (priceInCents) =>
        formatCurrency(priceInCents || 0, { currency: order?.currency });
    const paymentStatus = order.payment_status || order.status || 'processing';
    const isPaid = paymentStatus === 'paid';

    return (
        <div className="container" style={{ padding: '7rem 1rem 3rem 1rem', maxWidth: '720px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <FaCheckCircle size={80} color="var(--color-forest)" style={{ marginBottom: '1.5rem' }} />
                <h1>Payment Successful!</h1>
                <p style={{ fontSize: '1.1em', color: 'var(--color-ink-soft)', marginTop: '1rem' }}>
                    Order <strong>#{order.order_num}</strong> {isPaid ? 'has been confirmed.' : 'is being finalized.'}
                </p>
                <p style={{ color: 'var(--color-ink-soft)' }}>Payment status: {paymentStatus}</p>
                {!isPaid && (
                    <p style={{ color: 'var(--color-ink-soft)', marginTop: '0.5rem' }}>
                        You will receive a confirmation email once the payment is fully processed.
                    </p>
                )}
                <p style={{ color: 'var(--color-ink-soft)', marginTop: '1.5rem' }}>
                    Thank you for your order! You'll receive a confirmation email shortly.
                </p>
            </div>

            {order.items?.length ? (
                <div style={{ background: 'var(--color-surface)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 18px rgba(0, 86, 112, 0.12)', marginBottom: '2rem' }}>
                    <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem' }}>Items</h2>
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        {order.items.map((item, index) => (
                            <div key={`${item.name}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontWeight: 600 }}>{item.name}</div>
                                <div style={{ color: 'var(--color-ink-soft)', fontSize: '0.9rem' }}>
                                    Qty: {item.quantity}
                                    {item.variant_name ? ` • ${item.variant_name}` : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                    {order.total != null && (
                        <div style={{ borderTop: '1px solid var(--color-border)', marginTop: '1.5rem', paddingTop: '1rem', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                            <span>Total</span>
                            <span>{formatPrice(order.total)}</span>
                        </div>
                    )}
                </div>
            ) : null}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/shop" className="btn btn-primary">
                    Continue Shopping
                </Link>
                <Link to="/" className="btn btn-secondary">
                    Go to Home
                </Link>
            </div>
        </div>
    );
}

export default OrderSuccess;
